import { AppConstants } from '@app/app.constants';
import { UserSessionBo } from '@blueprints/session.blueprint';

export type DdlSuccessfulLogin = UserSessionBo | DdlSuccessfulLoginBo;

// a successfull login without any session info!
export class DdlSuccessfulLoginBo {}

export class DdlInputData {
	/*
	HOWTO add new input data:
	- add here
	  - field
	  - constructor
	  - isValid method
	- add it to the AppConstants.ROUTER_PARAMS
	- add it to the DdlComponent::readInputData using AppConstants.ROUTER_PARAMS
	 */

	code?: string = null;
	name?: string = null;
	username?: string = null;

	// static because we need this to test from the outside too!
	static isCodeValid(code: string): boolean {
		const codeConfig = AppConstants.DDL_INPUT_CODE_CONFIG;
		return code != null && code.length === codeConfig.numFields && code.split('').every((char) => codeConfig.regex.test(char));
	}

	constructor(code?: string, name?: string, username?: string) {
		this.code = code;
		this.name = name;
		this.username = username;
	}

	// This also controls if the local input data should be set which override sthe server input data!
	isValid(): boolean {
		return (this.code != null && DdlInputData.isCodeValid(this.code)) || this.name != null || this.username != null;
	}

	equals(other: DdlInputData): boolean {
		if (this === other) {
			return true;
		}

		if (other == null) {
			return false;
		}

		return this.code === other.code && this.name === other.name;
	}
}

export class DdlSsoData {
	token: string = null;
	tokenSignature: string = null;
	test: boolean = null;

	constructor(token?: string, tokenSignature?: string, test?: boolean) {
		this.token = token;
		this.tokenSignature = tokenSignature;
		this.test = test;
	}

	isValid(): boolean {
		return this.token != null && this.tokenSignature != null;
	}

	equals(other: DdlSsoData) {
		if (this === other) {
			return true;
		}

		if (other == null) {
			return false;
		}

		return this.token === other.token && this.tokenSignature === other.tokenSignature && this.test === other.test;
	}
}

export enum DdlEntryPoint {
	// Apple TV (we run on a second device)
	appleTv = 'appleTv',
	// Apple iOS (we run on a webview inside the app)
	appleIOS = 'appleIOS',
	// BigScreen and old AppleTV solution. Initially the only entry point!
	auth = 'auth',
	// Mobile Login Pages (running in Android App, and fallback for iOS)
	mlp = 'mlp',
	/*
	HOWTO add new entry point:
	- add here
	- add DdlStart<EntryPoint>Component, setting the entry point in the store
	- add method in DdlEntryPointHelper for easy check
	- add it to the AppConstants.SECTIONS and AppConstants.ROUTER_SECTIONS
	- add it to the delegated-device-login.routing.module (using SECTIONS)
	- add it to the UiHelper.isDelegatedDeviceLoginMode (using ROUTER_SECTIONS)
	 */
}

export class DdlBag {
	inputData: DdlInputData = null;
	ssoData: DdlSsoData = null;
	userRegistered: boolean = null;
	userAddedMultiroom: boolean = null;
	multistepToken: string = null;
	entryPoint: DdlEntryPoint = null;

	constructor(
		inputData: DdlInputData,
		ssoData: DdlSsoData,
		userRegistered: boolean,
		userAddedMultiroom: boolean,
		multistepToken: string,
		entryPoint: DdlEntryPoint
	) {
		this.inputData = inputData;
		this.ssoData = ssoData;
		this.userRegistered = userRegistered;
		this.userAddedMultiroom = userAddedMultiroom;
		this.multistepToken = multistepToken;
		this.entryPoint = entryPoint;
	}

	equals(other: DdlBag) {
		if (this === other) {
			return true;
		}

		if (other == null) {
			return false;
		}

		return (
			((this.inputData == null && other.inputData == null) || this.inputData?.equals(other.inputData)) &&
			((this.ssoData == null && other.ssoData == null) || this.ssoData?.equals(other.ssoData)) &&
			((this.userRegistered == null && other.userRegistered == null) || this.userRegistered === other.userRegistered) &&
			((this.userAddedMultiroom == null && other.userAddedMultiroom == null) ||
				this.userAddedMultiroom === other.userAddedMultiroom) &&
			((this.multistepToken == null && other.multistepToken == null) || this.multistepToken === other.multistepToken) &&
			((this.entryPoint == null && other.entryPoint == null) || this.entryPoint === other.entryPoint)
		);
	}
}

export enum DdlError {
	DEVICE_CODE_INVALID = 'DEVICE_CODE_INVALID',
	MULTISTEP = 'MULTISTEP',
	GENERAL_LOGIN_ERROR = 'GENERAL_LOGIN_ERROR',
}
